// src/utils/portalNav.js
// The portal's navigation rules (spec 5.1 and 5.3). The tab is a dimension of the URL inside the existing
// "home" phase, so every link already in a client's inbox (no tab param) keeps landing on Home, and d, t and
// the dev-only a always survive a move. Pure: no React, no router imports.
import { tabFeatureOn } from "./portalFeatures";

export const PORTAL_TABS = [
  { id: "home", label: "Home", icon: "home", weight: 1 },
  { id: "timeline", label: "Timeline", icon: "timeline", weight: 1 },
  { id: "openhouses", label: "Open Houses", icon: "sign", weight: 1.32 },
  { id: "forms", label: "Forms", icon: "forms", weight: 1 },
  // 10/01/2026: the Guide (questions and answers, with the seller video library at its foot) took the Videos
  // tab's place, so a seller's bar stays at five tabs and a buyer's grows to four.
  { id: "guide", label: "Guide", icon: "guide", weight: 1 },
];

const BUYER_TAB_IDS = ["home", "timeline", "forms", "guide"];

// Henry 2026-09-25: the Money Owed tab exists only while the client owes the agent money, so it is not one of
// the fixed tabs above. tabsForSide adds it last when asked; a client who owes nothing never sees it, and a
// saved ?tab=money link falls back to Home once everything is paid back (readPortalNav's allowed list).
export const MONEY_OWED_TAB = { id: "money", label: "Money Owed", icon: "money", weight: 1.32 };

// Henry 10/06/2026: the Market tab (Homes Like Yours, the active MLS homes in the seller's ZIP within $100,000
// of their list price) is sellers only and exists only while the morning snapshot has a home to show.
// Labelled Competition (Henry 10/06/2026); the id stays "market" so saved ?tab=market links keep working.
export const MARKET_TAB = { id: "market", label: "Competition", icon: "search", weight: 1.32 };

// Henry 10/06/2026 (October 6th meeting #13): the Net Sheet tab (the Estimate of Proceeds every seller signs)
// is sellers only and exists only while the agent has shared an estimate to the portal.
export const NET_SHEET_TAB = { id: "netsheet", label: "Net Sheet", icon: "forms", weight: 1.2 };

// Buyers get the four tabs that have content for them; everyone else gets all five. Either side gets Money
// Owed on the end while something is owed.
// Henry 10/06/2026 (Client Portal switches): `features` ({ timeline: false, ... } from the payload) drops every
// tab whose feature is off for this client. Home is never dropped. Absent = everything on.
export function tabsForSide(side, { moneyOwed = false, market = false, netSheet = false, features = null } = {}) {
  const s = String(side || "").trim().toLowerCase();
  const tabs = s === "buyer" ? PORTAL_TABS.filter((t) => BUYER_TAB_IDS.includes(t.id)) : PORTAL_TABS.slice();
  if (market && s !== "buyer") tabs.push(MARKET_TAB);
  if (netSheet && s !== "buyer") tabs.push(NET_SHEET_TAB);
  const all = moneyOwed ? [...tabs, MONEY_OWED_TAB] : tabs;
  return all.filter((t) => tabFeatureOn(features, t.id));
}

// "1fr 1fr 1.32fr 1fr 1fr": Open Houses is the one label that needs more room.
export function tabGridColumns(tabs) {
  return (Array.isArray(tabs) ? tabs : []).map((t) => `${(t && t.weight) || 1}fr`).join(" ");
}

const readParam = (params, key) =>
  params && typeof params.get === "function" ? String(params.get(key) || "") : "";

// -> { tab, formId }. A form always means the Forms tab; an unknown or side-hidden tab means Home.
export function readPortalNav(params, allowedTabIds) {
  const allowed = Array.isArray(allowedTabIds) ? allowedTabIds : PORTAL_TABS.map((t) => t.id);
  const formId = readParam(params, "form");
  // A ?form= link while Forms is switched off for this client (10/06/2026) lands on Home.
  if (formId) return allowed.includes("forms") ? { tab: "forms", formId } : { tab: "home", formId: "" };
  const raw = readParam(params, "tab");
  // The Videos tab folded into the Guide (10/01/2026): an old ?tab=videos link lands on the Guide.
  const tab = raw === "videos" ? "guide" : raw;
  return { tab: allowed.includes(tab) ? tab : "home", formId: "" };
}

// Copy every param, drop the old tab and form, then set the new ones. Home writes neither.
export function writePortalNav(params, { tab = "home", formId = "" } = {}) {
  const next = new URLSearchParams(params || undefined);
  next.delete("tab");
  next.delete("form");
  if (formId) {
    next.set("tab", "forms");
    next.set("form", formId);
  } else if (tab && tab !== "home") {
    next.set("tab", tab);
  }
  return next;
}

export function navSearch(params, nav) {
  return `?${writePortalNav(params, nav).toString()}`;
}

export const PORTAL_BASE_TITLE = "Team Edelstein Client Portal";

const titled = (label) => `${label} | ${PORTAL_BASE_TITLE}`;

// The browser tab's title for the screen on show (spec 4.0).
export function portalDocTitle({ phase, tab, formTitle } = {}) {
  if (phase === "loading") return PORTAL_BASE_TITLE;
  if (phase === "gate") return titled("Sign In");
  if (phase === "dead") return titled("Link Expired");
  if (phase === "error") return titled("Connection Problem");
  if (phase === "form") return titled(String(formTitle || "") || "Forms");
  const found = [...PORTAL_TABS, MARKET_TAB, NET_SHEET_TAB, MONEY_OWED_TAB].find((t) => t.id === tab);
  return titled(found ? found.label : "Home");
}
